import type { RegimeReforma, TributoLinhaResponse, TributosPrecoResponse } from '@triade/contracts';
import { formatarDecimal, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Rotulos pt-BR dos regimes da reforma (o nome da etapa vem da API). */
export const REGIME_ROTULO: Record<RegimeReforma, string> = {
  LEGACY: 'Legislação anterior',
  DUAL_2026: 'Teste 2026',
  TRANSICAO: 'Transição',
  NOVO_2033: 'Regime final',
};

export const NOTA_INFORMATIVO = 'Em 2026 CBS/IBS são destacados, não somam ao total (informativo).';

/** Selo "Alíquota de referência estimada" (aliquotaEstimada = true). Presentacional. */
export function SeloEstimada({ className }: { className?: string }) {
  return (
    <span
      data-testid="selo-estimada"
      className={cn('inline-flex items-center rounded-full border border-warning-dot/40 bg-warning-bg px-2 py-0.5 text-caption font-medium text-warning-ink', className)}
    >
      Alíquota de referência estimada
    </span>
  );
}

/** Tabela expansivel dos tributos da formacao (valores da API; so formata). */
export function TabelaTributos({ tributos }: { tributos: TributoLinhaResponse[] }) {
  return (
    <details data-testid="reforma-tributos" className="group rounded-lg border border-border bg-card px-3 py-2">
      <summary className="cursor-pointer text-small font-medium text-ink-body">Tributos ({tributos.length})</summary>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-left text-small">
          <thead className="text-caption text-ink-muted">
            <tr>
              <th scope="col" className="py-1 pr-3 font-medium">Tributo</th>
              <th scope="col" className="py-1 pr-3 font-medium">Classe</th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">Base</th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">Taxa efetiva</th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">Legal × fator</th>
              <th scope="col" className="py-1 text-right font-medium">Valor</th>
            </tr>
          </thead>
          <tbody>
            {tributos.map((t, i) => (
              <tr key={`${t.tributo}-${i}`} className="border-t border-border/60">
                <td className="py-1 pr-3">
                  {t.tributo}
                  {t.informative && <span className="ml-2 rounded-xs bg-line-soft px-1.5 py-0.5 text-caption">informativo</span>}
                </td>
                <td className="py-1 pr-3">{t.classe === 'por_dentro' ? 'Por dentro' : 'Por fora'}</td>
                <td className="tabular py-1 pr-3 text-right">{formatarMoeda(t.base)}</td>
                <td className="tabular py-1 pr-3 text-right">{formatarFracaoPercentual(t.rate)}</td>
                <td className="tabular py-1 pr-3 text-right">{celulaLegal(t)}</td>
                <td className="tabular py-1 text-right">{formatarMoeda(t.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function celulaLegal(t: TributoLinhaResponse): string {
  if (t.legalRate === null) return '—';
  const aliquota = formatarFracaoPercentual(t.legalRate);
  return t.factor === null ? aliquota : `${aliquota} × ${formatarDecimal(t.factor, 2)}`;
}

/** Preco sem CBS/IBS, "+ CBS/IBS" e Total da NF de um sugerido ou praticado (valores prontos da API). */
export function ValoresReforma({ valores, informativo, titulo }: { valores: TributosPrecoResponse; informativo: boolean; titulo: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-small font-semibold text-ink-body">{titulo}</h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-small">
        <dt className="text-ink-secondary">Preço (sem CBS/IBS)</dt>
        <dd className="tabular text-right font-medium">{formatarMoeda(valores.precoSemTributos)}</dd>
        <dt className="text-ink-secondary">{informativo ? '+ CBS/IBS (destacado, não soma)' : '+ CBS/IBS'}</dt>
        <dd className="tabular text-right">{formatarMoeda(valores.cbsIbs)}</dd>
        <dt className="text-ink-secondary">Total da NF</dt>
        <dd className="tabular text-right font-semibold">{formatarMoeda(valores.totalNf)}</dd>
      </dl>
    </div>
  );
}
